import {
  CreateTagInput as CreateInput,
  CreateTagMutationVariables as CreateMutationVariables,
  DeleteTagMutationVariables as DeleteMutationVariables,
  OnCreateTagSubscription,
  OnDeleteTagSubscription,
  OnUpdateTagSubscription,
  Tag,
  UpdateTagInput,
  UpdateTagMutationVariables as UpdateMutationVariables,
  cleanInputCreate,
  cleanInputUpdate,
  getReadId,
  getTableClientId,
  onCreateTag,
  onDeleteTag,
  onUpdateTag,
  createTag as createMutation,
  deleteTag as deleteMutation,
  getTag as getQuery,
  syncTags,
  updateTag as updateMutation,
  CreateTagInput,
  TAG_TABLE_NAME,
} from '@rentguru/commons-utils';
import {
  cleanMixedDatastoreAndAPISubscription,
  deleteEntityWithFetchBefore,
  getFilterFieldNameForIndex,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import { createContext, Reducer, useContext, useEffect, useMemo, useReducer } from 'react';

import { useUser } from './UserContext';

interface TagState {
  tags: Tag[];
  loading: boolean;
  shouldFetch: boolean;
}

type Action =
  | { type: 'SHOULD_FETCH' }
  | { type: 'IS_FETCHING' }
  | { type: 'FETCHED'; payload: { tags: Tag[] } }
  | { type: 'ADD'; payload: { tag: Tag } }
  | { type: 'UPDATE'; payload: { tag: Tag } }
  | { type: 'DELETE'; payload: { id: string } }
  | { type: 'FETCH_FAILED' };

const initialState: TagState = {
  tags: [],
  loading: false,
  shouldFetch: false,
};

const tagReducer = (state: TagState, action: Action): TagState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (state.loading || state.shouldFetch) return state;
      return { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      if (state.loading) return state;
      return { ...state, loading: true };
    case 'FETCHED':
      return { ...state, tags: action.payload.tags, loading: false, shouldFetch: false };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD':
      if (state.tags.find((tag) => tag.id === action.payload.tag.id)) return state;
      return { ...state, tags: [...state.tags, action.payload.tag] };
    case 'UPDATE': {
      return {
        ...state,
        tags: state.tags.map((tag) => (tag.id === action.payload.tag.id ? action.payload.tag : tag)),
      };
    }
    case 'DELETE':
      return { ...state, tags: state.tags.filter((tag) => tag.id !== action.payload.id) };
    default:
      return state;
  }
};

const fetchTags = async (by: 'byClientId', byValue: string): Promise<Tag[]> =>
  await list<Tag>(syncTags, getFilterFieldNameForIndex(by), byValue);

export interface TagContextState extends TagState {
  getTag: (id: string) => Tag | undefined;
  getTagsForTable: (foreignTableName: string) => Tag[];
  createTag: (input: Omit<CreateTagInput, 'id' | 'clientId' | 'readId'>) => Promise<Tag>;
  updateTag: (original: Tag, updates: Partial<Tag>) => Promise<Tag>;
  deleteTag: (id: string) => Promise<Tag | undefined>;
  setFetchTags: () => void;
}

export const TagContext = createContext<TagContextState | null>(null);

export const TagContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const { clientId } = useUser();
  const [state, dispatch] = useReducer<Reducer<TagState, Action>>(tagReducer, initialState);

  const setFetchTags = () => dispatch({ type: 'SHOULD_FETCH' });

  useEffect(() => {
    if (clientId) {
      dispatch({ type: 'SHOULD_FETCH' });
    }
  }, [clientId]);

  useEffect(() => {
    const fetchAndSet = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const result = await fetchTags('byClientId', getTableClientId(clientId!, TAG_TABLE_NAME));
        dispatch({ type: 'FETCHED', payload: { tags: result } });
      } catch (error) {
        console.error('[tags] fetching tags failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };
    if (state.shouldFetch) fetchAndSet();
  }, [state.shouldFetch, clientId]);

  useSubscriptions<OnCreateTagSubscription, OnUpdateTagSubscription, OnDeleteTagSubscription>(
    cleanMixedDatastoreAndAPISubscription(onCreateTag),
    cleanMixedDatastoreAndAPISubscription(onUpdateTag),
    cleanMixedDatastoreAndAPISubscription(onDeleteTag),
    (data) => dispatch({ type: 'ADD', payload: { tag: data.onCreateTag as Tag } }),
    (data) => dispatch({ type: 'UPDATE', payload: { tag: data.onUpdateTag as Tag } }),
    (data) => {
      const { id } = data.onDeleteTag as Tag;
      dispatch({ type: 'DELETE', payload: { id } });
    },
    undefined,
    undefined,
    undefined,
    true
  );

  const createTag = async (input: Omit<CreateTagInput, 'id' | 'clientId' | 'readId'>) => {
    const tag = await mutation<Tag, CreateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateInput),
        clientId: getTableClientId(clientId!, TAG_TABLE_NAME),
        readId: getReadId(clientId!, TAG_TABLE_NAME),
      },
    });
    dispatch({ type: 'ADD', payload: { tag } });
    return tag;
  };

  const updateTag = async (original: Tag, updates: Partial<Tag>) => {
    const result = await mutation<Tag, UpdateMutationVariables>(updateMutation, {
      input: cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) as UpdateTagInput,
    });
    dispatch({ type: 'UPDATE', payload: { tag: result } });
    return result;
  };

  const getTag = (id: string): Tag | undefined => state.tags.find((tag) => tag.id === id);

  const deleteTag = async (id: string) => {
    const deletedTag = getTag(id);
    await deleteEntityWithFetchBefore<Pick<Tag, 'id'>, DeleteMutationVariables>({ id }, getQuery, deleteMutation);
    dispatch({ type: 'DELETE', payload: { id } });
    return deletedTag;
  };

  const getTagsForTable = (foreignTableName: string): Tag[] =>
    state.tags.filter((tag) => tag.foreignTableName === foreignTableName);

  const values = useMemo(
    () => ({
      ...state,
      getTag,
      getTagsForTable,
      createTag,
      updateTag,
      deleteTag,
      setFetchTags,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <TagContext.Provider value={values}>{children}</TagContext.Provider>;
};

export const useTags = (): TagContextState => {
  const context = useContext<TagContextState | null>(TagContext);
  if (context === undefined) {
    throw new Error('`useTags` hook must be used within a `TagContextProvider` component');
  }
  return context as TagContextState;
};
